import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Banner from "../components/Banner";
import PrimeLayout from "../components/PrimeLayout";
import { ACT_LOG_TYPE, ActivityLogModuleTypeHead, ActivityLogPageTypeHead } from "../constants/Data";
import StyledTable from "../components/StyledTable";
import Pagination from "../components/elements/Pagination";
import { getLogs } from "../services/api/activityLog.api";
import moment from "moment";
import { Grid } from "@mui/material";
import StyledSelect from "../components/elements/StyledSelect";
import DatePicker from "../components/elements/DatePicker";
import StyledButton from "../components/elements/StyledButton";
import { BiSearchAlt } from "react-icons/bi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { formatRolesBackEndToFrontEnd } from "../utils/filterRoles";
import Footer from "../components/Footer";

export default function ActivityLog() {
  const [order, setOrder] = useState({ id: "date", sort: "asc" });
  const [page, setPage] = useState(1);
  const [logRowData, setLogRowData] = useState([]);
  const [paginatedData, setPaginatedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("init");
  const [role, setRole] = useState("init");
  const [email, setEmail] = useState("init");
  const [activityType, setActivityType] = useState("init");
  const [effectedTo, setEffectedTo] = useState("init");
  const [logType, setLogType] = useState(ACT_LOG_TYPE.MODULE_LOGS);
  const [pageName, setPageName] = useState("init");
  const [dateRange, setDateRange] = useState([null, null]);
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user?.user_role !== "admin" && user?.user_role !== "super_admin") {
        toast.error("You do not have access to this page");
        return navigate("/");
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // obtain local UTC offset and convert to msec to T00:00:00.000Z --> dateRange[0].getTimezoneOffset() * 60000
    const dateFrom = dateRange[0]
      ? new Date(
        dateRange[0] - dateRange[0].getTimezoneOffset() * 60000
      ).toISOString()
      : null;
    // obtain local UTC offset and convert to msec to 23:59:00.000Z --> dateRange[1].getTimezoneOffset() * ((23 + ( 59/ 60 ) + 5.5 ) / 5.5) * 60000
    const dateTo = dateRange[1]
      ? new Date(
        dateRange[1] -
        dateRange[1].getTimezoneOffset() *
        ((23 + 59 / 60 + 5.5) / 5.5) *
        60000
      ).toISOString()
      : null;
    fetchLogs(dateFrom, dateTo, page);
    // eslint-disable-next-line
  }, [page, order, navigate, logType]);

  const getPage = (p) => {
    setPage(p);
  };

  const fetchLogs = async (dateFrom, dateTo, page) => {
    try {
      setLoading(true);
      const logs = await getLogs({
        limit: 10,
        offset: page,
        key: order.id,
        order: order.sort,
        name: name === "init" ? undefined : name,
        email: email === "init" ? undefined : email,
        role: role,
        ...(logType === ACT_LOG_TYPE.MODULE_LOGS ? {
          activity_type: activityType === "init" ? undefined : activityType,
          effected_user: effectedTo === "init" ? undefined : effectedTo
        } : { page_name: pageName === "init" ? undefined : pageName }),
        dateFrom: dateFrom,
        dateTo: dateTo,
        log_type: logType
      });
      setPaginatedData(logs);
      const dataRows = logs.docs.map((doc) => {
        return {
          id: doc._id,
          name: doc.user.name,
          role: formatRolesBackEndToFrontEnd(doc.user.user_role),
          email: doc.user.email,
          activity_type: doc.activity_type,
          activity: doc.activity,
          effected_user: doc.effected_user?.name,
          page_name: doc.page_name,
          date: moment(doc.date).format("DD/MM/YYYY, HH:mm"),
        };
      });
      setLogRowData([...dataRows]);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.message === "No active account") {
        toast.error("Session Expired, Please Log back", {
          theme: "colored",
        });
        return navigate("/");
      }
    }
  };

  const searchLogs = async () => {
    // obtain local UTC offset and convert to msec to T00:00:00.000Z --> dateRange[0].getTimezoneOffset() * 60000
    const dateFrom = dateRange[0]
      ? new Date(
        dateRange[0] - dateRange[0].getTimezoneOffset() * 60000
      ).toISOString()
      : null;
    // obtain local UTC offset and convert to msec to 23:59:00.000Z --> dateRange[1].getTimezoneOffset() * ((23 + ( 59/ 60 ) + 5.5 ) / 5.5) * 60000
    const dateTo = dateRange[1]
      ? new Date(
        dateRange[1] -
        dateRange[1].getTimezoneOffset() *
        ((23 + 59 / 60 + 5.5) / 5.5) *
        60000
      ).toISOString()
      : null;
    await fetchLogs(dateFrom, dateTo, 1);
    setPage(1);
  };

  return (
    <div>
      <Header />
      <Banner small />
      <PrimeLayout title="Activity Log">
        <Grid
          container
          direction="row"
          justifyContent="left"
          alignItems="center"
          spacing={3}
        >
          <Grid item xs={12} md={3} lg={2}>
            <StyledSelect
              label={"Log Type"}
              defaultVal={"Select the Log Type"}
              options={[
                { label: "Module Logs", value: ACT_LOG_TYPE.MODULE_LOGS },
                { label: "Web Map Logs", value: ACT_LOG_TYPE.PAGE_LOGS }
              ]}
              control={logType === ACT_LOG_TYPE.MODULE_LOGS ? "Module Logs" : "Web Map Logs"}
              setControl={setLogType}
              isEmptyValAllowed={false}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={2}>
            <StyledSelect
              label={"Name"}
              defaultVal={"Select the Name"}
              options={paginatedData.names}
              control={name}
              setControl={setName}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={2}>
            <StyledSelect
              label={"Role"}
              defaultVal={"Select the Role"}
              options={[
                { label: "all users" },
                {
                  label: "Super Admin",
                },
                {
                  label: "Admin",
                },
              ]}
              control={role}
              setControl={setRole}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={2}>
            <StyledSelect
              label={"Email"}
              defaultVal={"Select Email"}
              options={paginatedData.email}
              control={email}
              setControl={setEmail}
            />
          </Grid>
          {logType === ACT_LOG_TYPE.MODULE_LOGS
            ? <>
              <Grid item xs={12} md={3} lg={2}>
                <StyledSelect
                  label={"Activity Type"}
                  defaultVal={"Select Activity Type"}
                  options={[
                    "Module Permissions",
                    "User Management",
                    "Module Management",
                  ]}
                  control={activityType}
                  setControl={setActivityType}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <StyledSelect
                  label={"Effected To"}
                  defaultVal={"Select Effected To"}
                  options={paginatedData.effectedTos}
                  control={effectedTo}
                  setControl={setEffectedTo}
                />
              </Grid>
            </>
            : <Grid item xs={12} md={3} lg={2}>
              <StyledSelect
                label={"Page Name"}
                defaultVal={"Select Page Name"}
                options={paginatedData.pageNames}
                control={pageName}
                setControl={setPageName}
              />
            </Grid>}
          <Grid item xs={12} md={4} lg={3}>
            <DatePicker dateRange={dateRange} setDateRange={setDateRange} />
          </Grid>
          <Grid item md={6} xs={12}>
            <StyledButton
              iconLeft={<BiSearchAlt size={25} />}
              variant={"outlined"}
              style={{ marginTop: "25px" }}
              onClick={() => {
                searchLogs();
              }}
            >
              Search
            </StyledButton>
          </Grid>
        </Grid>
        <StyledTable
          headCells={logType === ACT_LOG_TYPE.MODULE_LOGS
            ? ActivityLogModuleTypeHead
            : ActivityLogPageTypeHead}
          rows={logRowData}
          order={order}
          setOrder={setOrder}
          loading={loading}
        />
        {paginatedData?.totalPages > 1 && !loading && (
          <Pagination
            count={paginatedData?.totalPages}
            getPage={getPage}
            page={page}
          />
        )}
      </PrimeLayout>
      <Footer />
    </div>

  );
}
